import {
  type Component,
  createApp,
  defineComponent,
  h,
  onUnmounted,
  type PropType,
  type Ref,
  ref,
  useAttrs,
} from 'vue'
import type { AFormProps } from '@/components/business/AForm/types'

import lazyLoad from '@/utils/lazyLoad'
import setupApp from '@/utils/setupApp'
import AppContext from '@/components/AppContext.vue'
import { dynamicRenderElementId } from '@/constant/app'
import ContextServiceProvider from '@/components/ContextServiceProvider'
import AModal, { type AModalProps, type AModalSlots } from '@/components/AModal.vue'
import { AppI18n } from '@/libs/i18n'

const getScopeContainer = (parentNode?: HTMLElement) => {
  const container = document.createElement('div')

  parentNode
    ? parentNode.appendChild(container)
    : document.getElementById(dynamicRenderElementId)?.appendChild(container)

  return container
}

const dynamicRender = async (
  component: Component,
  componentProps?: AnyRecord,
  options?: { root?: HTMLElement; onUnmounted?: () => void },
) => {
  const container = getScopeContainer(options?.root)

  const app = await setupApp(
    createApp({
      setup() {
        onUnmounted(() => {
          container.remove()
          options?.onUnmounted?.()
        })

        return () =>
          h(AppContext, () => [
            h(ContextServiceProvider, null, {
              default: () => [
                h(component, {
                  ...componentProps,
                  onUnmounted: () => app.unmount(),
                }),
              ],
            }),
          ])
      },
    }),
  )

  app.mount(container)

  return {
    app,
    unmount: () => app.unmount(),
  }
}

type ModalOptions = {
  slots?: Partial<AModalSlots>
  modalProps?: AModalProps
  componentProps?: AnyRecord
}

interface ModalChildrenComponentProps {
  maximize: boolean
  getRef: () => Ref
}

dynamicRender.modal = (component: Component, options?: ModalOptions) => {
  const { slots, componentProps, modalProps } = options || {}

  const _modalProps = {
    cancelProps: { icon: 'pi pi-times' },
    confirmProps: { icon: 'pi pi-save' },
    ...modalProps,
  }

  return dynamicRender(
    defineComponent({
      setup() {
        const attrs = useAttrs()

        return () =>
          h(AModal, { ..._modalProps, ...attrs } as Any, {
            default: (props: ModalChildrenComponentProps) => {
              return [
                h(component, {
                  ref: props.getRef(),
                  maximize: props.maximize,
                  ...componentProps,
                }),
              ]
            },
            ...slots,
          })
      },
    }),
  )
}

dynamicRender.modalConfirm = (options: {
  size?: 'small' | 'large'
  title?: string
  message: string
  onSave: () => PromiseOptional
  modalProps?: Partial<AModalProps>
}) => {
  const ConfirmComponent = defineComponent({
    setup() {
      return () =>
        h('div', { class: 'flex items-center' }, [
          h('i', { class: 'pi pi-info-circle text-2xl' }),
          h('span', { class: 'ml-4' }, options.message),
        ])
    },
  })

  return dynamicRender.modal(ConfirmComponent, {
    modalProps: {
      size: options.size,
      header: options.title,
      confirm: options.onSave,
      cancelProps: { icon: 'pi pi-times' },
      confirmProps: { severity: 'danger', label: AppI18n.global.t('delete'), icon: 'pi pi-trash' },
      ...options.modalProps,
    },
  })
}

dynamicRender.modalSimpleForm = (options: {
  title?: string
  size?: 'small' | 'large'
  initialValues?: AnyRecord
  modalProps?: Partial<AModalProps>
  schema: AFormProps['schemaConfig']
  onSave: (formData: AnyRecord) => PromiseOptional<Any>
}) => {
  lazyLoad(() => import('@/components/business/AForm')).then(({ default: AForm }) => {
    const SimpleForm = defineComponent({
      props: {
        maximize: Boolean as PropType<ModalChildrenComponentProps['maximize']>,
      },

      setup(_, { expose }) {
        const form = ref()

        expose({
          onSave: () => form.value.submit(),
        })

        return () =>
          h(AForm, {
            ref: form,
            size: options.size,
            schemaConfig: options.schema,
            initialValues: options.initialValues,
          })
      },
    })

    return dynamicRender.modal(SimpleForm, {
      modalProps: {
        size: options.size,
        header: options.title,
        confirm: options.onSave,
        ...options.modalProps,
      },
    })
  })
}

export default dynamicRender
